import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader, RefreshCw, X } from 'lucide-react';
import useAnalyticsData from '../hooks/useAnalyticsData';
import { displayName, fmtDateTime, fmtDuration, fmtNumber, fmtSpan } from '../utils/analyticsFormat';

export const SORTS = {
  students: [['totalDurationMs', 'Active time'], ['name', 'Name'], ['sessions', 'Sessions'], ['edits', 'Edits'], ['commands', 'Commands'], ['clipboard', 'Clipboard'], ['lastSeen', 'Last activity']],
  labs: [['totalDurationMs', 'Active time'], ['name', 'Name'], ['students', 'Students'], ['sessions', 'Sessions'], ['lastSeen', 'Last activity']],
  ips: [['studentCount', 'Students'], ['sessions', 'Sessions'], ['totalDurationMs', 'Active time'], ['lastSeen', 'Last seen']],
  multi: [['externalIPCount', 'External addresses'], ['name', 'Name'], ['totalDurationMs', 'Active time']],
  concurrent: [['overlapMs', 'Overlap duration'], ['startedAt', 'Session date']],
  sessions: [['sessionStart', 'Session date'], ['durationMs', 'Duration'], ['eventCount', 'Events']],
};

export function AnalyticsTabs({ tabs, value, onChange, panelId, label }) {
  return <div className="analytics-tabs" role="tablist" aria-label={label}>
    {tabs.map((tab, index) => <button key={tab.key} type="button" role="tab"
      id={`${panelId}-tab-${tab.key}`} aria-controls={panelId} aria-selected={value === tab.key}
      tabIndex={value === tab.key ? 0 : -1} onClick={() => onChange(tab.key)}
      onKeyDown={(event) => {
        const direction = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        const next = direction ? (index + direction + tabs.length) % tabs.length
          : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
        if (next === null) return;
        event.preventDefault();
        onChange(tabs[next].key);
        event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
      }}>{tab.label}</button>)}
  </div>;
}

export function SearchField({ value = '', onChange, label = 'Search', placeholder = 'Search name, email or student ID…' }) {
  const [input, setInput] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => { setInput(value); }, [value]);
  useEffect(() => {
    if (input === value) return undefined;
    const timer = setTimeout(() => onChangeRef.current(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input, value]);
  return <label className="analytics-search">{label}
    <input type="search" value={input} maxLength={100} placeholder={placeholder} onChange={(event) => setInput(event.target.value)} />
  </label>;
}

export function QueryError({ query }) {
  return <div className="analytics-error" role="alert">
    <span>{query.error?.response?.data?.error || 'Unable to load analytics. Please try again.'}</span>
    <button type="button" className="btn btn-sm" onClick={query.refresh}>Retry</button>
  </div>;
}

export function Loading({ label = 'Loading analytics…' }) {
  return <div className="analytics-loading" role="status"><Loader size={18} className="spinner" aria-hidden="true" />{label}</div>;
}

export function Pagination({ pagination, onPage, busy, label = 'page' }) {
  if (!pagination) return null;
  const { page, limit, total, pages } = pagination;
  const first = total && page <= pages ? (page - 1) * limit + 1 : 0;
  return <div className="analytics-pager">
    <span role="status" aria-live="polite">Showing {fmtNumber(first)}–{fmtNumber(first ? Math.min(page * limit, total) : 0)} of {fmtNumber(total)}</span>
    <div className="analytics-pager-controls">
      {page > pages && page > 1 && <button className="btn btn-sm" type="button" onClick={() => onPage(1)}>First page</button>}
      <button className="btn btn-sm" type="button" aria-label={`Previous ${label}`} disabled={busy || page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft size={15} aria-hidden="true" /></button>
      <span>{page} / {Math.max(pages, 1)}</span>
      <button className="btn btn-sm" type="button" aria-label={`Next ${label}`} disabled={busy || page >= pages} onClick={() => onPage(page + 1)}><ChevronRight size={15} aria-hidden="true" /></button>
    </div>
  </div>;
}

export function TableToolbar({ kind, search, sort, direction, limit, onChange }) {
  const fields = SORTS[kind] || SORTS.students;
  return <div className="analytics-toolbar">
    <SearchField value={search} onChange={(value) => onChange({ search: value, page: 1 })}
      placeholder={kind === 'ips' ? 'Search an address…' : kind === 'labs' ? 'Search labs…' : 'Search name, email or student ID…'} />
    <label className="analytics-control">Sort by
      <select value={sort || fields[0][0]} onChange={(event) => onChange({ sort: event.target.value, direction: event.target.value === 'name' ? 'asc' : 'desc', page: 1 })}>
        {fields.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
    </label>
    <label className="analytics-control">Order
      <select value={direction || 'desc'} onChange={(event) => onChange({ direction: event.target.value, page: 1 })}>
        <option value="desc">Descending</option><option value="asc">Ascending</option>
      </select>
    </label>
    <label className="analytics-control">Rows per page
      <select value={limit} onChange={(event) => onChange({ limit: Number(event.target.value), page: 1 })}>
        {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
      </select>
    </label>
  </div>;
}

function Person({ row, onClick, disabled }) {
  const name = row.studentId ? displayName(row) : 'Unknown student';
  return <button className="analytics-link analytics-person" type="button" onClick={onClick} disabled={disabled || !row.studentId}>
    <strong title={name}>{name}</strong>
    <small title={row.email || row.studentId}>{row.email || row.studentId || 'No student ID recorded'}</small>
  </button>;
}

function SessionRow({ row, scope }) {
  const [open, setOpen] = useState(false);
  const [filePage, setFilePage] = useState(1);
  const query = useAnalyticsData(`sessions/${encodeURIComponent(row.id)}`, scope, open);
  const files = query.data?.filesTouched || [];
  return <>
    <tr>
      <td><button type="button" className="analytics-link" aria-expanded={open} aria-label={`Session details ${fmtDateTime(row.sessionStart)}`} onClick={() => setOpen(!open)}>{fmtSpan(row.sessionStart, row.sessionEnd)}</button></td>
      <td>{row.labName}</td><td>{fmtDuration(row.durationMs)}</td><td>{fmtNumber(row.editCount)}</td><td>{fmtNumber(row.fileCount)}</td>
    </tr>
    {open && <tr><td colSpan={5}>
      <div className="analytics-session-detail">
        {query.isError ? <QueryError query={query} /> : !query.data ? <Loading label="Loading session details…" /> : <>
          <p>{fmtNumber(query.data.saves)} saves · {fmtNumber(query.data.downloads)} downloads · {fmtNumber(query.data.focusChanges)} focus changes</p>
          <p className="analytics-muted">{query.data.ips?.slice(0, 5).map((item) => item.ip).join(' · ')}</p>
          <strong>Recorded files ({fmtNumber(files.length)})</strong>
          <ul>{files.slice((filePage - 1) * 25, filePage * 25).map((file) => <li key={file}>{file}</li>)}</ul>
          {files.length > 25 && <Pagination pagination={{ page: filePage, limit: 25, total: files.length, pages: Math.ceil(files.length / 25) }} onPage={setFilePage} label="file page" />}
          {!files.length && <p className="analytics-muted">No file paths recorded.</p>}
        </>}
      </div>
    </td></tr>}
  </>;
}

const HEADINGS = {
  students: ['Student', 'Sessions', 'Active time', 'Edits', 'Commands', 'Clipboard', 'External IPs', 'Last activity'],
  multi: ['Student', 'Sessions', 'Active time', 'Edits', 'Commands', 'Clipboard', 'External IPs', 'Last activity'],
  labs: ['Lab', 'Status', 'Students', 'Sessions', 'Active time', 'Edits', 'Last activity'],
  ips: ['Address', 'Students', 'Sessions', 'Active time', 'Labs', 'Last seen'],
  concurrent: ['Student', 'Overlap', 'First lab', 'Second lab', 'Session date'],
  sessions: ['Session', 'Lab', 'Duration', 'Edits', 'Files'],
};

export function AnalyticsTable({ query, kind, onStudent, onIP, onLab, onPage, scope = {}, label = 'Results', pagerLabel }) {
  if (query.isError) return <QueryError query={query} />;
  if (!query.data) return <Loading />;
  const items = (query.data.items || []).slice(0, Math.min(query.data.pagination?.limit || 25, 100));
  const disabled = query.isPlaceholderData;
  if (!items.length) return <>
    <div className="analytics-empty card">No matching results. Try another search or scope.</div>
    <Pagination pagination={query.data.pagination} busy={query.isFetching} onPage={onPage} label={pagerLabel} />
  </>;
  return <>
    <div className="analytics-table-frame">
      <table className="analytics-table" aria-label={label} aria-busy={query.isFetching}>
        <thead><tr>{HEADINGS[kind].map((heading) => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
        <tbody>
          {items.map((row) => {
            if (kind === 'sessions') return <SessionRow key={row.id} row={row} scope={scope} />;
            if (kind === 'students' || kind === 'multi') return <tr key={row.studentId}>
              <td><Person row={row} disabled={disabled} onClick={() => onStudent(row.studentId)} /></td>
              <td>{fmtNumber(row.sessions)}</td><td>{fmtDuration(row.totalDurationMs)}</td><td>{fmtNumber(row.edits)}</td>
              <td>{fmtNumber(row.commands)}</td><td>{fmtNumber(row.clipboard)}</td><td>{fmtNumber(row.externalIPCount)}</td><td>{fmtDateTime(row.lastSeen)}</td>
            </tr>;
            if (kind === 'labs') return <tr key={row.labId}>
              <td><button type="button" className="analytics-link analytics-person" onClick={() => onLab(row.labId)} disabled={disabled}><strong title={row.name}>{row.name}</strong><small>{row.courseCode}{row.section ? ` · Section ${row.section}` : ''}</small></button></td>
              <td>{row.status}</td><td>{fmtNumber(row.students)}</td><td>{fmtNumber(row.sessions)}</td><td>{fmtDuration(row.totalDurationMs)}</td><td>{fmtNumber(row.edits)}</td><td>{fmtDateTime(row.lastSeen)}</td>
            </tr>;
            if (kind === 'ips') return <tr key={row.ip}>
              <td><button type="button" className="analytics-link analytics-mono" onClick={() => onIP(row.ip)} disabled={disabled}>{row.ip}</button>
                {row.internal && <span className="analytics-tag">internal</span>}{row.shared && <span className="analytics-tag">shared network</span>}
                <div className="analytics-preview">{row.studentPreview?.slice(0, 3).map(displayName).join(', ')}{row.studentCount > 3 ? ` +${row.studentCount - 3} more` : ''}</div>
              </td>
              <td>{fmtNumber(row.studentCount)}</td><td>{fmtNumber(row.sessions)}</td><td>{fmtDuration(row.totalDurationMs)}</td><td>{fmtNumber(row.labCount)}</td><td>{fmtDateTime(row.lastSeen)}</td>
            </tr>;
            return <tr key={row.id}>
              <td><Person row={row} disabled={disabled} onClick={() => onStudent(row.studentId)} /></td><td>{fmtDuration(row.overlapMs)}</td>
              <td>{row.firstLab}</td><td>{row.secondLab}</td><td>{fmtDateTime(row.startedAt)}</td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
    <Pagination pagination={query.data.pagination} busy={query.isFetching} onPage={onPage} label={pagerLabel} />
  </>;
}

function Timeline({ timeline }) {
  const [selected, setSelected] = useState(0);
  const buckets = timeline?.buckets || [];
  const max = Math.max(1, ...buckets.map((row) => row.events));
  const active = buckets[Math.min(selected, Math.max(0, buckets.length - 1))];
  return <>
    {buckets.length ? <svg viewBox={`0 0 ${Math.max(buckets.length * 12, 120)} 180`} preserveAspectRatio="none"
      role="img" tabIndex={0} aria-label={`Activity events per ${timeline.bucket}, UTC. Use left and right arrows to inspect dates.`}
      onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        setSelected((current) => Math.max(0, Math.min(buckets.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1))));
      }}>
      {[45, 90, 135, 175].map((y) => <line key={y} x1="0" x2="100%" y1={y} y2={y} stroke="var(--border-primary)" />)}
      {buckets.map((row, index) => {
        const height = Math.max(2, row.events / max * 166);
        return <rect key={row.bucket} x={index * 12} y={175 - height} width="9" height={height} rx="2" fill="var(--accent-primary)" opacity={selected === index ? 1 : 0.65} onMouseEnter={() => setSelected(index)}><title>{row.bucket}: {row.events} events</title></rect>;
      })}
    </svg> : <div className="analytics-empty">No sessions started in this date range.</div>}
    <div className="analytics-chart-caption" role="status">{active ? `${active.bucket} UTC · ${fmtNumber(active.events)} events · ${fmtNumber(active.sessions)} sessions · ${fmtNumber(active.students)} students` : 'Choose a different scope or date range.'}</div>
  </>;
}

export function AnalyticsOverview({ query, onExplore, onStudent, onIP, bucket, onBucket }) {
  if (query.isError) return <QueryError query={query} />;
  if (!query.data) return <Loading />;
  const { summary: s, signals, timeline, freshness } = query.data;
  const tiles = [
    ['Active students', fmtNumber(s.students), `${fmtNumber(s.sessions)} sessions`],
    ['Active time', fmtDuration(s.totalDurationMs), `Median ${fmtDuration(s.medianDurationMs)} / session`],
    ['Edits', fmtNumber(s.edits), `${fmtNumber(s.events)} events`],
    ['Commands', fmtNumber(s.commands)], ['Clipboard uses', fmtNumber(s.clipboard)],
    ['External addresses', fmtNumber(s.distinctIPs), `${fmtNumber(s.internalIPs)} internal addresses excluded`],
  ];
  return <div aria-busy={query.isFetching}>
    <div className="analytics-stats">{tiles.map(([title, value, sub]) => <div className="analytics-stat" key={title}><span>{title}</span><strong>{value}</strong>{sub && <small>{sub}</small>}</div>)}</div>
    <section className="analytics-chart card">
      <div className="analytics-chart-header"><h2>Activity events over time</h2><label className="analytics-control">Time bucket
        <select value={bucket} onChange={(event) => onBucket(event.target.value)}><option value="day">Daily</option><option value="hour">Hourly (up to 7 days)</option></select>
      </label></div>
      {bucket === 'hour' && timeline.bucket !== 'hour' && <p className="analytics-muted">Showing daily totals. Choose a date range of seven days or less for hourly detail.</p>}
      <Timeline timeline={timeline} />
    </section>
    <div className="analytics-signals">
      {[
        { key: 'shared', title: 'Shared network addresses', description: 'Several students used the same external address. Campus networks can be shared.' },
        { key: 'multi', title: 'Multiple addresses', description: 'Students who used more than one external address in this scope.' },
        { key: 'concurrent', title: 'Overlapping sessions', description: 'Inspect recorded sessions whose activity times overlap.' },
      ].map(({ key, title, description }) => <section key={key} className="card analytics-signal-card" aria-label={title}>
        <header><h2>{title}</h2><span className="analytics-tag">{fmtNumber(signals[key].pagination.total)}</span></header>
        <p>{description}</p>
        {signals[key].items.slice(0, 5).map((row) => <div className="analytics-signal-row" key={row.id || row.ip || row.studentId}>
          <button type="button" className="analytics-link" onClick={() => key === 'shared' ? onIP(row.ip) : onStudent(row.studentId)}>{key === 'shared' ? row.ip : displayName(row)}</button>
          <span>{key === 'shared' ? `${fmtNumber(row.studentCount)} students` : key === 'multi' ? `${row.externalIPCount} addresses` : fmtDuration(row.overlapMs)}</span>
        </div>)}
        {!signals[key].items.length && <div className="analytics-muted">None recorded in this scope.</div>}
        <footer><button className="analytics-link" type="button" onClick={() => onExplore('signals', key)}>View all {fmtNumber(signals[key].pagination.total)}</button></footer>
      </section>)}
    </div>
    <p className="analytics-freshness">Last collected {fmtDateTime(freshness.harvestedAt)} · Collection runs every 15 minutes. Session-date filters use the start of each session; the timeline is shown in UTC.</p>
  </div>;
}

export function AnalyticsDetail({ entity, scope, tab, table, onChange, onClose, onBack, backLabel, onStudent, onIP, onLab }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current.querySelector('button')?.focus();
    const keydown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const focusable = [...ref.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),a[href],[tabindex="0"]')].filter((element) => element.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (!ref.current.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first)?.focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  useEffect(() => { ref.current?.querySelector('button')?.focus(); }, [entity.type, entity.id]);
  const student = entity.type === 'student';
  const root = `${student ? 'students' : 'ips'}/${encodeURIComponent(entity.id)}`;
  const summaryQ = useAnalyticsData(root, scope);
  const tabs = student ? ['sessions', 'ips', 'labs'] : ['students', 'labs'];
  const selected = tabs.includes(tab) ? tab : tabs[0];
  const kind = selected;
  const listQ = useAnalyticsData(`${root}/${selected}`, { ...scope, ...table }, !summaryQ.isError);
  const data = summaryQ.data;
  const name = student ? displayName(data?.student || { studentId: entity.id }) : entity.id;
  return createPortal(<div className="analytics-drawer-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside ref={ref} className="analytics-drawer" role="dialog" aria-modal="true" aria-labelledby="analytics-detail-heading">
      <header className="analytics-drawer-header">
        <button type="button" className="analytics-link" onClick={onBack}><ArrowLeft size={16} aria-hidden="true" /><span className="analytics-ellipsis">{backLabel}</span></button>
        <h2 id="analytics-detail-heading">{student ? 'Student activity' : 'Address activity'}</h2>
        <button className="btn btn-sm" type="button" aria-label="Refresh analytics details" disabled={summaryQ.isFetching || listQ.isFetching} onClick={() => { summaryQ.refresh(); listQ.refresh(); }}><RefreshCw size={15} aria-hidden="true" /></button>
        <button className="btn btn-sm" type="button" aria-label="Close analytics details" onClick={onClose}><X size={18} aria-hidden="true" /></button>
      </header>
      <div className="analytics-drawer-body">
        {summaryQ.isError ? <QueryError query={summaryQ} /> : !data ? <Loading label="Loading details…" /> : <>
          <section className="analytics-detail-identity">
            <h3>{name}</h3>
            {student ? <p>{data.student.studentId}{data.student.email ? ` · ${data.student.email}` : ''}</p> : <p>{data.internal ? 'Internal cluster address' : 'External client address'} · {fmtNumber(data.studentCount)} students</p>}
            <div className="analytics-detail-metrics"><span>{fmtNumber(student ? data.totals.sessions : data.sessions)} sessions</span><span>{fmtDuration(student ? data.totals.totalDurationMs : data.totalDurationMs)} active</span><span>{fmtNumber(student ? data.counts.labs : data.labCount)} labs</span></div>
          </section>
          <AnalyticsTabs value={selected} onChange={(value) => onChange({ tab: value, page: 1, sort: '', search: '', direction: 'desc' })}
            tabs={tabs.map((key) => ({ key, label: key === 'ips' ? 'Addresses' : key[0].toUpperCase() + key.slice(1) }))} panelId="analytics-detail-panel" label="Detail breakdown" />
          <div role="tabpanel" id="analytics-detail-panel" aria-labelledby={`analytics-detail-panel-tab-${selected}`}>
            {selected !== 'sessions' ? <TableToolbar kind={kind} {...table} onChange={onChange} /> : <div className="analytics-toolbar">
              <label className="analytics-control">Session order<select value={table.direction} onChange={(event) => onChange({ direction: event.target.value, page: 1 })}><option value="desc">Newest first</option><option value="asc">Oldest first</option></select></label>
              <label className="analytics-control">Rows per page<select value={table.limit} onChange={(event) => onChange({ limit: Number(event.target.value), page: 1 })}>{[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
            </div>}
            <AnalyticsTable query={listQ} kind={kind} scope={scope} onPage={(page) => onChange({ page })} onStudent={onStudent} onIP={onIP} onLab={onLab} label={`${student ? 'Student' : 'Address'} ${selected}`} pagerLabel="detail page" />
          </div>
        </>}
      </div>
    </aside>
  </div>, document.body);
}
